import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

export const env = createEnv({
  /*
   * Serverside Environment variables, not available on the client.
   * Will throw if you access these variables on the client.
   */
  server: {
    AWS_ACCESS_KEY_ID: z.string().min(1),
    AWS_SECRET_ACCESS_KEY: z.string().min(1),
    AWS_DEFAULT_REGION: z.string().min(1),
    DATABASE_URL: z.string().url(),
    CELCOIN_API_URL: z.string().url(),
    NEXT_PUBLIC_BRASIL_API_URL: z.string().url(),
    CELCOIN_AUTH_API_URL: z.string().url(),
    CELCOIN_API_ORIGINATOR_ID: z.string().min(1),
    CELCOIN_API_ORIGINATOR_SECRET: z.string().min(1),
    CELCOIN_API_FUNDING_ID: z.string().min(1),
    CELCOIN_API_FUNDING_SECRET: z.string().min(1),
    CELCASH_API_URL: z.string().url(),
    CELCASH_GALAXY_ID: z.string().min(1),
    CELCASH_GALAXY_HASH: z.string().min(1),
    CELCASH_TRANSACTION_STATUS_UPDATE_WEBHOOK_CONFIRM_HASH: z
      .string()
      .optional(),
    NEOCREDIT_API_URL: z.string().url(),
    NEOCREDIT_API_TOKEN: z.string().min(1),
    NEOCREDIT_API_CALLBACK_URL: z.string().url(),
    NEOCREDIT_API_CALLBACK_UPDATE_CARD_COLUMN_URL: z.string().url(),
    VIA_CEP_API_URL: z.string().url(),
    ROBBU_MESSAGE_API_URL: z.string().url(),
    ROBBU_INVENIO_PRIVATE_TOKEN: z.string(),
    ROBBU_API_TOKEN: z.string(),
    ROBBU_WHATSAPP_COUNTRY_CODE: z.string().max(3),
    ROBBU_WHATSAPP_PHONE_NUMBER: z.string().min(11),
    JWT_SECRET: z.string().min(1),
    TWILIO_PHONE_NUMBER: z.string().min(1).optional(),
    TWILIO_ACCOUNT_SID: z.string().min(1).optional(),
    TWILIO_AUTH_TOKEN: z.string().min(1).optional(),
    TWILIO_MESSAGING_SERVICE_SID: z.string().min(1).optional(),
    RESEND_API_KEY: z.string().min(1),
    SENTRY_AUTH_TOKEN: z.string().min(1).optional(),
    ADMIN_WHATSAPP_PHONE: z.string().min(11),
    PARTNER_CSJ_API_KEY: z.string().min(1),
    PARTNER_ROBBU_API_KEY: z.string().min(1),
    SLACK_INTERNAL_CS_UPDATES_WEBHOOK_URL: z.string().min(1),
    PARTNER_YGGY_BASE_URL: z.string().url(),
    PARTNER_YGGY_PTOKEN: z.string().min(1),
  },
  /*
   * Environment variables available on the client (and server).
   *
   * 💡 You'll get type errors if these are not prefixed with NEXT_PUBLIC_.
   */
  client: {
    NEXT_PUBLIC_DOMAIN_URL: z.string().url(),
    NEXT_PUBLIC_ENVIRONMENT: z.enum(['local', 'preview', 'production']),
    NEXT_PUBLIC_BRASIL_API_URL: z.string().url(),
  },
  /*
   * Due to how Next.js bundles environment variables on Edge and Client,
   * we need to manually destructure them to make sure all are included in bundle.
   *
   * 💡 You'll get type errors if not all variables from `server` & `client` are included here.
   */
  runtimeEnv: {
    AWS_ACCESS_KEY_ID: process.env.AWS_ACCESS_KEY_ID,
    AWS_SECRET_ACCESS_KEY: process.env.AWS_SECRET_ACCESS_KEY,
    AWS_DEFAULT_REGION: process.env.AWS_DEFAULT_REGION,
    DATABASE_URL: process.env.DATABASE_URL,
    CELCOIN_API_URL: process.env.CELCOIN_API_URL,
    NEXT_PUBLIC_BRASIL_API_URL: process.env.NEXT_PUBLIC_BRASIL_API_URL,
    CELCOIN_AUTH_API_URL: process.env.CELCOIN_AUTH_API_URL,
    CELCOIN_API_ORIGINATOR_ID: process.env.CELCOIN_API_ORIGINATOR_ID,
    CELCOIN_API_ORIGINATOR_SECRET: process.env.CELCOIN_API_ORIGINATOR_SECRET,
    CELCOIN_API_FUNDING_ID: process.env.CELCOIN_API_FUNDING_ID,
    CELCOIN_API_FUNDING_SECRET: process.env.CELCOIN_API_FUNDING_SECRET,
    CELCASH_API_URL: process.env.CELCASH_API_URL,
    CELCASH_GALAXY_ID: process.env.CELCASH_GALAXY_ID,
    CELCASH_GALAXY_HASH: process.env.CELCASH_GALAXY_HASH,
    CELCASH_TRANSACTION_STATUS_UPDATE_WEBHOOK_CONFIRM_HASH:
      process.env.CELCASH_TRANSACTION_STATUS_UPDATE_WEBHOOK_CONFIRM_HASH,
    NEOCREDIT_API_URL: process.env.NEOCREDIT_API_URL,
    NEOCREDIT_API_TOKEN: process.env.NEOCREDIT_API_TOKEN,
    NEOCREDIT_API_CALLBACK_URL: process.env.NEOCREDIT_API_CALLBACK_URL,
    NEOCREDIT_API_CALLBACK_UPDATE_CARD_COLUMN_URL:
      process.env.NEOCREDIT_API_CALLBACK_UPDATE_CARD_COLUMN_URL,
    VIA_CEP_API_URL: process.env.VIA_CEP_API_URL,
    ROBBU_MESSAGE_API_URL: process.env.ROBBU_MESSAGE_API_URL,
    ROBBU_INVENIO_PRIVATE_TOKEN: process.env.ROBBU_INVENIO_PRIVATE_TOKEN,
    ROBBU_API_TOKEN: process.env.ROBBU_API_TOKEN,
    ROBBU_WHATSAPP_COUNTRY_CODE: process.env.ROBBU_WHATSAPP_COUNTRY_CODE,
    ROBBU_WHATSAPP_PHONE_NUMBER: process.env.ROBBU_WHATSAPP_PHONE_NUMBER,
    JWT_SECRET: process.env.JWT_SECRET,
    TWILIO_PHONE_NUMBER: process.env.TWILIO_PHONE_NUMBER,
    TWILIO_ACCOUNT_SID: process.env.TWILIO_ACCOUNT_SID,
    TWILIO_AUTH_TOKEN: process.env.TWILIO_AUTH_TOKEN,
    TWILIO_MESSAGING_SERVICE_SID: process.env.TWILIO_MESSAGING_SERVICE_SID,
    NEXT_PUBLIC_DOMAIN_URL: process.env.NEXT_PUBLIC_DOMAIN_URL,
    NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    RESEND_API_KEY: process.env.RESEND_API_KEY,
    SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
    NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
    ADMIN_WHATSAPP_PHONE: process.env.ADMIN_WHATSAPP_PHONE,
    PARTNER_CSJ_API_KEY: process.env.PARTNER_CSJ_API_KEY,
    PARTNER_ROBBU_API_KEY: process.env.PARTNER_ROBBU_API_KEY,
    SLACK_INTERNAL_CS_UPDATES_WEBHOOK_URL:
      process.env.SLACK_INTERNAL_CS_UPDATES_WEBHOOK_URL,
    PARTNER_YGGY_BASE_URL: process.env.PARTNER_YGGY_BASE_URL,
    PARTNER_YGGY_PTOKEN: process.env.PARTNER_YGGY_PTOKEN,
  },
})
